import { buildSelectionKey } from './selectionKey';
import { sliceRange } from './selectionRange';
import { toMasterProductEntry, toMasterVariantEntry } from './toSelectionEntry';

export const getVariantIds = (variants = []) => variants.map(({ _id }) => _id);

const getProductKey = (productId) => buildSelectionKey({ type: 'master', _id: productId });
const getVariantKey = (variantId) => buildSelectionKey({ type: 'masterVariant', _id: variantId });

export const isBulkHighlighted = (isVariant, nodeId, selection) => {
  const key = isVariant ? getVariantKey(nodeId) : getProductKey(nodeId);
  return Boolean(selection.byKey[key]);
};

export const getVariantCheckboxState = (variantId, selection) => (
  Boolean(selection.byKey[getVariantKey(variantId)])
);

export const getProductCheckboxState = (productId, variantIds, selection) => {
  const productSelected = Boolean(selection.byKey[getProductKey(productId)]);
  const selectedVariantCount = variantIds.filter((id) => selection.byKey[getVariantKey(id)]).length;

  if (!productSelected && selectedVariantCount === 0) {
    return false;
  }

  if (variantIds.length === 0) {
    return productSelected;
  }

  if (productSelected && selectedVariantCount === variantIds.length) {
    return true;
  }

  return 'indeterminate';
};

export const getProductGroupTogglePlan = (productId, variantIds, productNode, selection) => {
  const checkboxState = getProductCheckboxState(productId, variantIds, selection);

  if (checkboxState === true) {
    return {
      addEntries: [],
      removeKeys: [
        getProductKey(productId),
        ...variantIds.map((id) => getVariantKey(id)),
      ],
    };
  }

  const addEntries = [toMasterProductEntry(productNode || { _id: productId })];
  (productNode?.variants || variantIds.map((id) => ({ _id: id }))).forEach((variant) => {
    addEntries.push(toMasterVariantEntry({
      ...variant,
      baseProductId: productId,
    }));
  });

  return { addEntries, removeKeys: [] };
};

export const getVariantTogglePlan = (variant, value, selection) => {
  const key = getVariantKey(variant._id);

  if (value === false || selection.byKey[key]) {
    return { addEntries: [], removeKeys: [key] };
  }

  return {
    addEntries: [toMasterVariantEntry(variant)],
    removeKeys: [],
  };
};

export const flattenVisibleBulkNodes = (products, expanded) => {
  const flat = [];

  products.forEach((product) => {
    flat.push({
      nodeId: product._id,
      isVariant: false,
      node: product,
      productId: product._id,
    });

    if (expanded.includes(product._id) && product.variants?.length) {
      product.variants.forEach((variant) => {
        flat.push({
          nodeId: variant._id,
          isVariant: true,
          node: variant,
          productId: product._id,
        });
      });
    }
  });

  return flat;
};

export const getBulkRangeNodes = (flatNodes, fromId, toId) => (
  sliceRange(flatNodes, fromId, toId, ({ nodeId }) => nodeId) ?? []
);

export const getRangeAddEntries = (rangeNodes) => {
  const entries = [];
  const seen = new Set();

  rangeNodes.forEach(({ isVariant, nodeId, node, productId }) => {
    if (isVariant) {
      const key = getVariantKey(nodeId);
      if (seen.has(key)) return;
      seen.add(key);
      entries.push(toMasterVariantEntry({
        ...node,
        _id: nodeId,
        baseProductId: productId,
      }));
      return;
    }

    const key = getProductKey(nodeId);
    if (seen.has(key)) return;
    seen.add(key);
    entries.push(toMasterProductEntry(node || { _id: nodeId }));
    getVariantIds(node?.variants).forEach((variantId) => {
      const variantKey = getVariantKey(variantId);
      if (seen.has(variantKey)) return;
      seen.add(variantKey);
      const variant = node.variants.find(({ _id }) => _id === variantId);
      entries.push(toMasterVariantEntry({
        ...variant,
        _id: variantId,
        baseProductId: nodeId,
      }));
    });
  });

  return entries;
};
